import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { Appointment } from '../api/appointments.ts';
import {
  formatHour,
  formatTimeRange,
  localMinutesSinceMidnight,
} from '../format/formatDateTime.ts';
import type { DateTimePreference } from '../format/dateTimePreference.ts';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { statusTone } from './statusActions.ts';
import { usePatient } from './usePatient.ts';

/**
 * A real calendar grid: a time axis down the left, one column per `columns` entry across the top,
 * each appointment positioned and sized by its actual time within that column — replacing
 * `SchedulePage`'s earlier plain Monday-Sunday list of `Panel`s (that page's own header comment on why
 * that first pass "chose a list over a calendar grid nobody had time to make responsive"), per direct
 * user feedback 2026-08-18: "the calendar view needs to look like a calendar, not individual
 * appointments."
 *
 * `columns` is deliberately generic rather than hardcoded to "one column per weekday" — `SchedulePage`
 * hands this component 7 day-columns for a single provider's week, or one column per org member for
 * the "all providers" day view, and this component only ever renders "a column, with a heading, full
 * of appointments," never anything provider- or day-specific itself.
 *
 * **No overlap-resolution logic, and that is not an oversight.** Every appointment within one column
 * belongs to exactly one provider on exactly one day, and `server/src/scheduling/service.ts`'s own
 * conflict-detection query (`hasOverlap`) already refuses two active appointments for the same
 * provider whose times overlap — so two blocks in the same column can never need to render
 * side-by-side. A column with two different providers' appointments (which this component never
 * receives — `SchedulePage` always partitions by provider) would need that logic; this one does not.
 *
 * **Each column's `min-w-[6.5rem]` is measured, not guessed on paper** — `hooks/useMediaQuery.ts`'s
 * own reasoning on why *this* breakpoint choice is not table-min-width-derived the way
 * `tableBreakpointRem` is. A first pass used `9rem`, which reads fine in isolation but, checked live
 * at a real 1440px browser width against `AppShell.tsx`'s `max-w-6xl` content column (1152px) minus
 * its nav rail and `Panel`'s own padding — roughly 824px actually available to a 7-day week grid —
 * left the grid needing an internal horizontal scroll to reach Friday through Sunday, which is a
 * materially worse "looks like a calendar" experience than the same 7 columns fitting outright.
 * `6.5rem` (104px) × 7 plus the time axis fits inside that same ~824px with margin to spare,
 * confirmed live the same way. The tradeoff is real and accepted: a long appointment-type string can
 * now truncate inside a block at this width where it previously might not have — the block's own
 * `title` attribute and a click-through to the reschedule dialog are the mitigation, matching every
 * other truncated cell in this app (`AppointmentList`'s own Patient/Type columns).
 */

/** Pixels-per-hour, expressed in `rem` so it scales with the user's own root font size rather than a
 * bare pixel count. 5rem (80px) at a 30-minute appointment (this feature's own shortest real slot,
 * `nineToNineThirty`-shaped fixtures throughout the test suite) gives a 2.5rem-tall block — enough for
 * two short lines of `text-xs` before `MIN_BLOCK_HEIGHT_REM` below ever has to intervene. */
const HOUR_HEIGHT_REM = 5;

/** A floor under `HOUR_HEIGHT_REM`'s own linear scaling — without it, a very short appointment (a
 * quick check-in) would render as a sliver too thin to read or reliably click. */
const MIN_BLOCK_HEIGHT_REM = 2;

/** The business-hours default the visible range never shrinks below, so an empty view still reads as
 * a real day rather than a zero-height grid with nothing to anchor it. */
const DEFAULT_RANGE_START_HOUR = 8;
const DEFAULT_RANGE_END_HOUR = 18;

export interface CalendarColumn {
  readonly key: string;
  readonly heading: ReactNode;
  readonly appointments: readonly Appointment[];
}

/** `[start, end)` in local hours, clamped to `[0, 24]`, covering every appointment actually being
 * displayed plus the business-hours default above — expanded, never shrunk, so a 7 AM or 7 PM
 * appointment is never clipped off the grid it is supposedly shown on. */
function hourRange(appointments: readonly Appointment[]): { start: number; end: number } {
  let start = DEFAULT_RANGE_START_HOUR;
  let end = DEFAULT_RANGE_END_HOUR;
  for (const appointment of appointments) {
    start = Math.min(start, Math.floor(localMinutesSinceMidnight(appointment.startsAt) / 60));
    end = Math.max(end, Math.ceil(localMinutesSinceMidnight(appointment.endsAt) / 60));
  }
  const clampedStart = Math.max(0, Math.min(start, 23));
  const clampedEnd = Math.min(24, Math.max(end, clampedStart + 1));
  return { start: clampedStart, end: clampedEnd };
}

/** The same status→colour decision `AppointmentList`'s `statusToneClass` renders as text colour,
 * rendered here as a block's left-edge accent instead — see `statusActions.ts`'s `statusTone`. */
function statusBorderClass(status: Appointment['status']): string {
  switch (statusTone(status)) {
    case 'accent':
      return 'border-l-accent-text';
    case 'success':
      return 'border-l-success';
    case 'danger':
      return 'border-l-danger';
    case 'warning':
      return 'border-l-warning';
    case 'muted':
      return 'border-l-line';
  }
}

function AppointmentBlock({
  appointment,
  rangeStartHour,
  preference,
  onSelect,
}: {
  readonly appointment: Appointment;
  readonly rangeStartHour: number;
  readonly preference: DateTimePreference;
  readonly onSelect: (appointment: Appointment) => void;
}) {
  const { t } = useTranslation('schedule');
  const patient = usePatient(appointment.patientId);
  const startMinutes = localMinutesSinceMidnight(appointment.startsAt);
  const endMinutes = localMinutesSinceMidnight(appointment.endsAt);
  const topRem = ((startMinutes - rangeStartHour * 60) / 60) * HOUR_HEIGHT_REM;
  const heightRem = Math.max(
    ((endMinutes - startMinutes) / 60) * HOUR_HEIGHT_REM,
    MIN_BLOCK_HEIGHT_REM,
  );
  const timeLabel = formatTimeRange(appointment.startsAt, appointment.endsAt, preference);
  const patientLabel =
    patient.data === undefined
      ? patient.isError
        ? t('list.unknownPatient')
        : t('list.loading')
      : `${patient.data.lastName}, ${patient.data.firstName}`;

  return (
    <button
      type="button"
      onClick={() => {
        onSelect(appointment);
      }}
      className={`absolute inset-x-0.5 overflow-hidden rounded-control border border-l-4 border-line bg-surface px-1.5 py-1 text-left text-xs leading-tight text-ink shadow-panel hover:bg-raised ${statusBorderClass(appointment.status)}`}
      style={{ top: `${String(topRem)}rem`, height: `${String(heightRem)}rem` }}
      title={`${timeLabel} — ${patientLabel} — ${appointment.appointmentType}`}
    >
      <span className="block truncate font-medium">{timeLabel}</span>
      <span className="block truncate">{patientLabel}</span>
    </button>
  );
}

export function CalendarGrid({
  columns,
  onSelect,
}: {
  readonly columns: readonly CalendarColumn[];
  readonly onSelect: (appointment: Appointment) => void;
}) {
  const { preference } = useDateTimeFormat();
  const allAppointments = columns.flatMap((column) => column.appointments);
  const { start: rangeStartHour, end: rangeEndHour } = hourRange(allAppointments);
  const hours = Array.from({ length: rangeEndHour - rangeStartHour }, (_, i) => rangeStartHour + i);
  const totalHeightRem = hours.length * HOUR_HEIGHT_REM;

  return (
    <div className="overflow-x-auto">
      {/* `flex` (block-level), not `inline-flex` (shrink-to-fit): an `inline-flex` wrapper sizes
          itself to its own content's natural/max-content width regardless of each column's
          `min-w-[6.5rem]`, which measured live as every header rendering at its own full
          natural text width (~165px) instead of shrinking toward the declared minimum — the
          well-known flexbox trap where an indefinite-width ancestor makes `flex-1`'s `flex-basis:
          0%` moot. A plain block-level `flex flex-col` fills this wrapper's own `overflow-x-auto`
          parent (bounded by the Panel it sits in), and default `align-items: stretch` then gives
          each header/body row a real, definite width to distribute across — which is what lets
          `min-w-[6.5rem]` actually act as a floor instead of being silently ignored. */}
      <div className="flex flex-col">
        <div className="flex">
          <div className="w-12 shrink-0 sm:w-16" aria-hidden="true" />
          {columns.map((column) => (
            <div
              key={column.key}
              className="min-w-[6.5rem] flex-1 truncate border-b border-line px-2 py-2 text-center text-sm font-semibold text-ink"
              title={typeof column.heading === 'string' ? column.heading : undefined}
            >
              {column.heading}
            </div>
          ))}
        </div>

        <div className="flex">
          <div
            className="relative w-12 shrink-0 sm:w-16"
            style={{ height: `${String(totalHeightRem)}rem` }}
            aria-hidden="true"
          >
            {hours.map((hour, index) => (
              <div
                key={hour}
                className="absolute right-2 -translate-y-1/2 text-xs whitespace-nowrap text-ink-muted"
                style={{ top: `${String(index * HOUR_HEIGHT_REM)}rem` }}
              >
                {formatHour(hour, preference)}
              </div>
            ))}
          </div>

          {columns.map((column) => (
            <div
              key={column.key}
              className="relative min-w-[6.5rem] flex-1 border-l border-line"
              style={{ height: `${String(totalHeightRem)}rem` }}
            >
              {hours.map((hour, index) => (
                <div
                  key={hour}
                  className="absolute inset-x-0 border-t border-line"
                  style={{ top: `${String(index * HOUR_HEIGHT_REM)}rem` }}
                />
              ))}
              {column.appointments.map((appointment) => (
                <AppointmentBlock
                  key={appointment.id}
                  appointment={appointment}
                  rangeStartHour={rangeStartHour}
                  preference={preference}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
